import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import {
  getAiHubStatusChipSx,
  type AiHubStatusTone,
} from "../aiHubChipStyles";

export const LIST_FAILED_STATUS_HINT =
  "This assessment did not complete and produced no results.";
export const LIST_PARTIAL_STATUS_HINT =
  "The assessment finished, but some pairs could not be scored.";

const statusHintIconSx = {
  fontSize: 14,
  transform: "rotate(180deg)",
} as const;

type AssessmentStatusChipProps = Readonly<{
  label: string;
  tone: AiHubStatusTone;
  /** When true, always render the circled exclamation (even without hint text). */
  showInfoIcon?: boolean;
  /** Tooltip shown only when hovering the circled exclamation icon. */
  hint?: string | null;
  component?: "div" | "span";
  sx?: Record<string, unknown>;
}>;

/**
 * Assessment status pill. Failed / Partially (and any chip with `hint`) show a
 * circled exclamation; tooltip appears only on that icon, not the whole chip.
 */
export default function AssessmentStatusChip({
  label,
  tone,
  showInfoIcon = false,
  hint,
  component = "div",
  sx,
}: AssessmentStatusChipProps) {
  const trimmedHint = hint?.trim() || "";
  const showIcon = showInfoIcon || Boolean(trimmedHint);

  return (
    <Chip
      component={component}
      size="small"
      label={
        showIcon ? (
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            <Box component="span">{label}</Box>
            {trimmedHint ? (
              <Tooltip
                title={trimmedHint}
                arrow
                placement="top"
                enterDelay={0}
                slotProps={{
                  popper: { sx: { zIndex: 2000 } },
                }}
              >
                <Box
                  component="span"
                  tabIndex={0}
                  aria-label={trimmedHint}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    lineHeight: 0,
                    cursor: "default",
                  }}
                  onClick={(event) => event.stopPropagation()}
                >
                  <ErrorOutlineIcon sx={statusHintIconSx} />
                </Box>
              </Tooltip>
            ) : (
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  lineHeight: 0,
                }}
              >
                <ErrorOutlineIcon sx={statusHintIconSx} />
              </Box>
            )}
          </Box>
        ) : (
          label
        )
      }
      sx={{
        ...getAiHubStatusChipSx(tone),
        ...(showIcon
          ? {
              minWidth: "auto",
              overflow: "visible",
              "& .MuiChip-label": {
                display: "inline-flex",
                alignItems: "center",
                overflow: "visible",
                padding: "4px 8px",
                lineHeight: "14px",
              },
            }
          : null),
        ...sx,
      }}
    />
  );
}

export function listAssessmentStatusHint(
  status: "completed" | "failed" | "partial" | "inProgress",
): string | null {
  if (status === "failed") return LIST_FAILED_STATUS_HINT;
  if (status === "partial") return LIST_PARTIAL_STATUS_HINT;
  return null;
}
